/**
 * Game State Creation — constructs the initial GameState object.
 */

import type { GameState } from '../types';
import { WORLD, PHYSICS, TURN_FLOW, VOID, GRAPPLE } from '../constants';
import { rollNewWind } from './wind';
import { createPlayerDefaults } from './player-defaults';
import { createModeState } from './mode';
import { DEFAULT_STAGE_ID } from '../stages';
import { getGroundYBelow } from '../terrain/grid';

export function createGameState(
  spawnXs: number[],
  getGroundY: (x: number) => number,
  terrainSeed: number,
  terrainBiome: string,
  terrainFeature: string | null,
  stageLeft: number,
  stageRight: number,
  spawnYs?: number[],
): GameState {
  return {
    players: spawnXs.map((sx, i) => {
      let sy: number;
      if (spawnYs && spawnYs[i] != null) {
        // Start scan 20px above editor spawn point to find the nearest ground surface below
        sy = getGroundYBelow(sx, spawnYs[i] - 20) - PHYSICS.ECB_RADIUS;
      } else {
        sy = getGroundY(sx) - PHYSICS.ECB_RADIUS;
      }
      return createPlayerDefaults(sx, sy, 'mortar', i, 'vs_local');
    }),
    currentPlayer: 0,
    startingPlayer: 0,
    phase: 'title_screen',
    projectiles: [],
    deferredSpawns: [],
    nextProjectileId: 1,
    firingPhaseKnockedPlayers: [],
    turnCount: 0,
    turnTimer: 0,
    frameCount: 0,
    hashCheckpointId: 0,
    freezeUntil: 0,
    winner: null,
    gameOverTimer: 0,
    roundNumber: 0,
    turnNumber: 1,
    wind: rollNewWind(),
    windShiftPending: false,
    windShiftTimer: 0,
    voidY: WORLD.VIRTUAL_HEIGHT - VOID.INITIAL_Y_OFFSET,
    voidTargetY: WORLD.VIRTUAL_HEIGHT - VOID.INITIAL_Y_OFFSET,
    voidRiseStartY: WORLD.VIRTUAL_HEIGHT - VOID.INITIAL_Y_OFFSET,
    voidRisePending: false,
    voidRiseTimer: 0,
    voidLastErodedY: WORLD.VIRTUAL_HEIGHT,
    voidErosionCollapseNeeded: false,
    turnCountdown: TURN_FLOW.TURN_DURATION,
    voidRetaliationQueue: [],
    voidRetaliationActive: false,
    voidMood: 0,
    voidMealCount: 0,
    voidMealX: 0,
    terrainSeed,
    terrainBiome,
    terrainFeature,
    stageId: DEFAULT_STAGE_ID,
    stageLeft,
    stageRight,
    grapple: {
      active: null,
      ammo: spawnXs.map(() => GRAPPLE.STARTING_AMMO),
    },
    vortexHoles: [],
    collapseActive: false,
    collapseTimer: 0,
    collapsePortals: [],
    beamActive: false,
    beamTimer: 0,
    beamPath: null,
    beamDisplayTimer: 0,
    beamPortalDetonations: [],
    diActivePlayer: -1,
    diWindowTimer: 0,
    diHitQueue: [],
    diWaitForAuthority: false,
    burstActivePlayer: -1,
    burstWindowTimer: 0,
    burstWaitForAuthority: false,
    burstFromSpecial: false,
    burstFromDefense: false,
    defenseQueue: [],
    defenseActivePlayer: -1,
    defensePreFreezeStates: new Map(),
    defenseWaitForAuthority: false,
    edgeguardChargeFrame: 0,
    edgeguardChargeTarget: 0,
    edgeguardFired: false,
    edgeguardKnockedIndices: [],
    edgeguardBeam: null,
    lotterySlots: [],
    lotteryTimer: 0,
    lotteryLockIndex: 0,
    lotteryPickTimeout: 0,
    roundStartTimer: 0,
    roundStartFireReleased: true,
    smoothingRegions: [],
    tankSelectReady: [false, false],
    tankSelectCursor: [0, 0],
    stageSelectCursor: 0,
    stageSelectReady: false,
    randomPreviewSeed: 0,
    randomPreviewThemeId: 'neon_void',
    menuSelectIndex: 0,
    menuTransitionTimer: 0,
    paused: false,
    pauseSelectIndex: 0,
    multiplayer: null,
    modeState: createModeState('vs_local'),
    twoGamepadsConnected: false,
    trickSession: null,
    trickScores: [0, 0],
    missStreak: [0, 0],
    matchStats: {
      damageDealt: [0, 0],
      damageTaken: [0, 0],
      bestSingleHit: [0, 0],
      shotsFired: [0, 0],
      voidKills: [0, 0],
      skyKills: [0, 0],
      kills: [0, 0],
      bestCombo: [0, 0],
      bestTurnScore: [0, 0],
      totalTrickScore: [0, 0],
    },
    postMatchReady: false,
    postMatchCursor: 0,
    coinsAwarded: false,
    // Skins shop
    skinsShopTank: 0,
    skinsShopSection: 0,
    skinsShopItem: 0,
    // Glitch events
    activeGlitch: 0,
  };
}
